/**
 * Big Screen / Superscreen / Corner Screen Uploader Page
 * 
 * Layout: Screen type at top → Upload section (teal highlight) → Notes below
 * Light theme with dark header and teal accents
 * Prepared Files section appears below uploader when files are uploaded
 */

import React, { useState, useCallback, useRef } from "react";
import { useVFContext } from "../../../context/VFContext";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { useToast } from "../../../components/ui/UploaderToast";
import UploaderSection from "../../../components/uploaders/UploaderSection";
import UserDetailsFields from "../../../components/uploaders/UserDetailsFields";
import UploadSuccessModal from "../../../components/modal/UploadSuccessModal";
import {
  SCREEN_PLACEMENTS,
  UPLOADER_SPECS,
} from "../../../config/bigScreenSpecs";
import { generateSessionId, generateCrockfordTimeId } from "../../../services/idGenerator";

// ============================================================
// WEBHOOK
// ============================================================
const METADATA_WEBHOOK = "https://hook.eu2.make.com/hs49hn7asl4ow6bx92kx1js28jpehkrt";
//const METADATA_WEBHOOK = "https://hook.eu2.make.com/dd68kvmmcbolqaejfitrrrvl5wiualvq";

// ============================================================
// STYLES
// ============================================================
const styles = {
  // Page
  page: {
    minHeight: "100vh",
    backgroundColor: "#f7f7f5",
    fontFamily: "'Space Grotesk', -apple-system, sans-serif",
  },
  
  // Dark header
  header: {
    backgroundColor: "#111114",
    padding: "18px 32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: "#fff",
    margin: 0,
  },
  headerSub: {
    fontSize: 13,
    color: "rgba(255,255,255,0.5)",
    marginTop: 2,
  },
  venueBadge: {
    backgroundColor: "rgba(47, 214, 181, 0.15)",
    border: "1px solid rgba(47, 214, 181, 0.4)",
    color: "#2fd6b5",
    padding: "6px 14px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
  },
  
  // Teal accent line under header
  accentLine: {
    height: 3,
    background: "linear-gradient(90deg, #2fd6b5, #84cc16)",
  },
  
  // Content
  content: {
    padding: "32px 40px 48px",
    maxWidth: 1100,
    margin: "0 auto",
  },
  
  // Section labels
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: 12,
  },
  
  // Screen type selector
  typeSection: {
    marginBottom: 28,
  },
  typeRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  typeBtn: {
    padding: "10px 18px",
    fontSize: 13,
    fontWeight: 500,
    color: "#555",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.15s ease",
    fontFamily: "inherit",
  },
  typeBtnHover: {
    borderColor: "#bbb",
    backgroundColor: "#fafafa",
    color: "#333",
  },
  typeBtnActive: {
    backgroundColor: "#111114",
    borderColor: "#111114",
    color: "#fff",
    fontWeight: 600,
  },
  
  // Upload section with teal highlight
  uploadSection: {
    backgroundColor: "#fff",
    borderRadius: 14,
    border: "2px solid #2fd6b5",
    boxShadow: "0 0 0 4px rgba(47, 214, 181, 0.1), 0 4px 20px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
    marginBottom: 28,
  },
  uploadHeader: {
    backgroundColor: "#111114",
    padding: "14px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  uploadTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  uploadTitleIcon: {
    color: "#2fd6b5",
  },
  uploadBody: {
    padding: 24,
  },
  
  // Prepared files section (appears when files uploaded)
  preparedSection: {
    marginTop: 0,
    maxHeight: 0,
    opacity: 0,
    overflow: "hidden",
    transition: "all 0.3s ease",
  },
  preparedSectionVisible: {
    marginTop: 20,
    maxHeight: 200,
    opacity: 1,
  },
  preparedBox: {
    backgroundColor: "#f0fdf9",
    border: "1px solid #2fd6b5",
    borderRadius: 10,
    padding: "16px 20px",
  },
  preparedHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  preparedTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  preparedTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111",
  },
  preparedCount: {
    backgroundColor: "#2fd6b5",
    color: "#000",
    fontSize: 11,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 10,
  },
  preparedHint: {
    fontSize: 12,
    color: "#666",
  },
  
  // Notes section
  notesSection: {
    marginBottom: 32,
  },
  textarea: {
    width: "100%",
    padding: "14px 16px",
    fontSize: 14,
    color: "#333",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 10,
    resize: "vertical",
    minHeight: 80,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  textareaFocus: {
    borderColor: "#2fd6b5",
    boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.1)",
  },
  charCount: {
    fontSize: 11,
    color: "#999",
    textAlign: "right",
    marginTop: 6,
  },
  
  // Footer
  footer: {
    paddingTop: 24,
    borderTop: "1px solid #e5e5e3",
    display: "flex",
    justifyContent: "flex-end",
  },
  submitBtn: {
    padding: "12px 32px",
    fontSize: 15,
    fontWeight: 600,
    color: "#000",
    background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "all 0.15s ease",
  },
  submitBtnHover: {
    transform: "translateY(-1px)",
    boxShadow: "0 6px 16px rgba(47, 214, 181, 0.35)",
  },
  submitBtnDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    transform: "none",
    boxShadow: "none",
  },
  
  // UserDetailsFields compatibility
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginBottom: 28,
  },
  field: {},
  label: {
    fontSize: 11,
    fontWeight: 700,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: 8,
    display: "block",
  },
  input: {
    width: "100%",
    padding: "11px 14px",
    fontSize: 14,
    color: "#333",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  inputFocus: {
    borderColor: "#2fd6b5",
    boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.1)",
  },
  
  // Placeholder state
  placeholderBox: {
    backgroundColor: "#fafaf8",
    borderRadius: 10,
    border: "2px dashed #ddd",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    color: "#999",
    fontSize: 14,
    textAlign: "center",
    height: "100%",
    minHeight: 200,
  },
};

// ============================================================
// TYPE BUTTON
// ============================================================
function TypeButton({ label, isActive, onClick, disabled }) {
  const [hovered, setHovered] = useState(false);
  
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...styles.typeBtn,
        ...(hovered && !isActive ? styles.typeBtnHover : {}),
        ...(isActive ? styles.typeBtnActive : {}),
        ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
      }}
    >
      {label}
    </button>
  );
}

// ============================================================
// CONSTANTS
// ============================================================
const MAX_DESC = 250;

// ============================================================
// MAIN COMPONENT
// ============================================================
export function BigScreenPage({ venue }) {
  const context = useVFContext();
  const { email: authEmail, name: authName } = useAuthUser();
  const { showError, ToastComponent } = useToast();

  const activeVenue = venue || context.venue || "MCG";
  const placements = SCREEN_PLACEMENTS[activeVenue] || [];

  const [sessionId, setSessionId] = useState(() => generateSessionId());
  const [submissionTimeId, setSubmissionTimeId] = useState(() => generateCrockfordTimeId());
  const [name, setName] = useState("");
  const [email, setEmail] = useState(authEmail || "");
  const [description, setDescription] = useState("");
  const [activePlacement, setActivePlacement] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);

  const [uploadResults, setUploadResults] = useState({});
  const activeUploads = useRef(0);

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastUploadInfo, setLastUploadInfo] = useState(null);

  const activeSpec = activePlacement ? UPLOADER_SPECS[activePlacement] : null;

  const handleUploadComplete = useCallback(({ uploadId, file }) => {
    activeUploads.current = Math.max(0, activeUploads.current - 1);
    setUploadResults((prev) => ({
      ...prev,
      [uploadId]: { ...prev[uploadId], assets: [...(prev[uploadId]?.assets || []), file] },
    }));
  }, []);

  const handleUploadStart = useCallback(() => {
    activeUploads.current += 1;
  }, []);

  const handleUploadCancel = useCallback(({ uploadId, fileId }) => {
    activeUploads.current = Math.max(0, activeUploads.current - 1);
    setUploadResults((prev) => ({
      ...prev,
      [uploadId]: { ...prev[uploadId], assets: prev[uploadId]?.assets?.filter((a) => a.id !== fileId) || [] },
    }));
  }, []);

  const uploadCount = uploadResults[activePlacement]?.assets?.length || 0;

  const resetForm = useCallback(() => {
    activeUploads.current = 0;
    setUploadResults({});
    setSessionId(generateSessionId());
    setSubmissionTimeId(generateCrockfordTimeId());
    setDescription("");
    setActivePlacement(null);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!activePlacement) {
      showError("Please select a screen type", { duration: 4000 });
      return;
    }
    if (activeUploads.current > 0) {
      showError("Please wait for uploads to finish", { duration: 4000 });
      return;
    }
    if (uploadCount === 0) {
      showError("Please upload at least one file", { duration: 4000 });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        sessionId,
        submissionParent: context.submissionParent,
        completedAt: new Date().toISOString(),
        name: authName || name || context.submitterName,
        email: authEmail || email,
        description,
        venue: activeVenue,
        ContentUpload: {
          [activePlacement]: {
            ledType: activeSpec?.ledType,
            contentGroup: activeSpec?.contentGroup,
            assets: uploadResults[activePlacement]?.assets || [],
          },
        },
      };

      const res = await fetch(METADATA_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`Failed (${res.status})`);
      
      // Build assets array - extract unique ID from each file's prefixedFileName
      // prefixedFileName format: "CAR_WEJTV0A_BS_OriginalName.jpg"
      // We want: "CAR_WEJTV0A"
      const uploadedAssets = (uploadResults[activePlacement]?.assets || []).map((asset, index) => {
        // Parse the prefixedFileName to extract CLUB_TIMEID
        // Format: CLUB_TIMEID_PREFIX_filename.ext
        const prefixedName = asset?.prefixedFileName || "";
        const parts = prefixedName.split("_");
        
        // First two parts are CLUB and TIMEID
        const contentId = parts.length >= 2 
          ? `${parts[0]}_${parts[1]}`
          : `${context.submitterShortName || "VF"}_${submissionTimeId}`;
        
        return {
          contentId,
          fileName: asset?.originalFileName || `File ${index + 1}`,
        };
      });
      
      // Show success modal
      setLastUploadInfo({
        assets: uploadedAssets,
        destination: activeSpec?.label || activePlacement,
      });
      setShowSuccessModal(true);
      
      resetForm();
    } catch (err) {
      console.error(err);
      showError("Something went wrong. Please try again.", { duration: 6000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = activeVenue === "Marvel" ? "Screen Assets" : "Big Screen Assets";
  const subtitle = activeVenue === "Marvel" 
    ? "Upload content for corner screens and superscreens" 
    : "Upload content for the MCG big screen";

  return (
    <div style={styles.page}>
      {/* Dark Header */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>{title}</h1>
          <p style={styles.headerSub}>{subtitle}</p>
        </div>
        <span style={styles.venueBadge}>{activeVenue} Stadium</span>
      </header>
      
      {/* Teal accent line */}
      <div style={styles.accentLine} />

      {/* Content */}
      <main style={styles.content}>
        <form onSubmit={handleSubmit}>
          
          {/* User Details (non-auth) */}
          <UserDetailsFields
            name={name}
            setName={setName}
            email={email}
            setEmail={setEmail}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            styles={styles}
          />

          {/* Screen Type - at top */}
          <div style={styles.typeSection}>
            <div style={styles.sectionLabel}>Screen Type</div>
            <div style={styles.typeRow}>
              {placements.map((p) => (
                <TypeButton
                  key={p.placementId}
                  label={p.label}
                  isActive={activePlacement === p.placementId}
                  onClick={() => {
                    setActivePlacement(p.placementId);
                    setUploadResults({});
                  }}
                  disabled={isSubmitting}
                />
              ))}
            </div>
          </div>

          {/* Upload Section - teal highlighted */}
          <div style={styles.uploadSection}>
            <div style={styles.uploadHeader}>
              <h2 style={styles.uploadTitle}>
                <svg style={styles.uploadTitleIcon} width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 3v9M5 7l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 13v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Upload Files
              </h2>
            </div>
            
            <div style={styles.uploadBody}>
              {activePlacement && activeSpec ? (
                <>
                  {/* Uploader - key forces remount when placement changes */}
                  <UploaderSection
                    key={activePlacement}
                    uploadId={activePlacement}
                    spec={activeSpec}
                    context={context}
                    sessionId={sessionId}
                    submissionTimeId={submissionTimeId}
                    onUploadComplete={handleUploadComplete}
                    onUploadStart={handleUploadStart}
                    onUploadCancel={handleUploadCancel}
                  />
                  
                  {/* Prepared Files - transitions in when files uploaded */}
                  <div style={{
                    ...styles.preparedSection,
                    ...(uploadCount > 0 ? styles.preparedSectionVisible : {}),
                  }}>
                    <div style={styles.preparedBox}>
                      <div style={styles.preparedHeader}>
                        <div style={styles.preparedTitleRow}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: "#2fd6b5" }}>
                            <path d="M3 8l4 4 6-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span style={styles.preparedTitle}>Prepared Files</span>
                          <span style={styles.preparedCount}>{uploadCount}</span>
                        </div>
                        <span style={styles.preparedHint}>Ready for submission</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div style={styles.placeholderBox}>
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ marginBottom: 12, color: "#ccc" }}>
                    <path d="M20 8v16M12 16l8-8 8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6 28v4a2 2 0 002 2h24a2 2 0 002-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <p>Select a screen type above to start uploading</p>
                </div>
              )}
            </div>
          </div>

          {/* Notes Section - below upload */}
          <div style={styles.notesSection}>
            <div style={styles.sectionLabel}>Notes (Optional)</div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESC))}
              onFocus={() => setFocusedField("notes")}
              onBlur={() => setFocusedField(null)}
              placeholder="Add any notes for the operations team..."
              style={{
                ...styles.textarea,
                ...(focusedField === "notes" ? styles.textareaFocus : {}),
              }}
            />
            <div style={styles.charCount}>{description.length}/{MAX_DESC}</div>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <button
              type="submit"
              disabled={isSubmitting || !activePlacement || uploadCount === 0}
              onMouseEnter={() => setIsSubmitHovered(true)}
              onMouseLeave={() => setIsSubmitHovered(false)}
              style={{
                ...styles.submitBtn,
                ...(isSubmitHovered && !isSubmitting && activePlacement && uploadCount > 0 ? styles.submitBtnHover : {}),
                ...((isSubmitting || !activePlacement || uploadCount === 0) ? styles.submitBtnDisabled : {}),
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </form>
      </main>

      {ToastComponent}
      
      {/* Upload Success Modal */}
      <UploadSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        assets={lastUploadInfo?.assets || []}
        destination={lastUploadInfo?.destination}
      />
    </div>
  );
}

// ============================================================
// EXPORTS
// ============================================================
export function MCGBigScreenPage() {
  return <BigScreenPage venue="MCG" />;
}

export function MarvelScreensPage() {
  return <BigScreenPage venue="Marvel" />;
}

export default BigScreenPage;
